/**
 * Converts the Manager UI's working draft into the override JSON that gets
 * persisted. We save only editable override slices (not the full merged
 * schema) so feature toggles and section edits round-trip reliably.
 *
 * `system` metadata is stripped before saving since it's computed by
 * applySystemFieldConstraints at render time.
 */

import defaultConfig from "../doctor-console/config/schema/defaultDoctorConsole.config.json";

const stripSystemMetadata = (node) => {
  if (Array.isArray(node)) {
    node.forEach(stripSystemMetadata);
    return node;
  }
  if (node && typeof node === "object") {
    delete node.system;
    Object.values(node).forEach(stripSystemMetadata);
  }
  return node;
};

const stripSectionIcons = (sections = []) =>
  sections.map(({ icon: _icon, ...section }) => section);

// Autosave firing faster than this stops being a "draft save" and starts hammering the API.
const MIN_AUTOSAVE_INTERVAL_MS = 15000;

const cloneFeatures = (features = {}) => {
  const cloned = JSON.parse(JSON.stringify(features));
  if (cloned.autosaveDraft?.intervalMs != null) {
    cloned.autosaveDraft.intervalMs = Math.max(
      MIN_AUTOSAVE_INTERVAL_MS,
      Number(cloned.autosaveDraft.intervalMs) || 0,
    );
  }
  return cloned;
};

export const buildOverrideFromDraft = (draftConfig) => {
  const override = {
    schemaVersion: draftConfig.schemaVersion ?? defaultConfig.schemaVersion ?? "1.0.0",
    sections: stripSectionIcons(draftConfig.sections ?? []),
    fieldSets: draftConfig.fieldSets ?? {},
    features: cloneFeatures(draftConfig.features ?? defaultConfig.features),
  };

  if (draftConfig.dependencies) {
    override.dependencies = draftConfig.dependencies;
  }

  return stripSystemMetadata(override);
};
